"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyzeSentenceOpeners,
  type OpenerReport,
  type OpenerSentence,
  type OpenerType,
} from "@/lib/tool-kit/writing-analysis/sentence-openers";

/** English strings. A translated content file overrides these through `ui`. */
export const SENTENCE_OPENER_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "The study examined how first-year students manage their time. The participants completed a weekly diary for eight weeks. The diaries recorded hours spent on coursework, paid work and leisure. The results showed that most students underestimated their study time. This suggests that self-reports are unreliable. It is clear that diaries offer a better measure. There are, however, limits to this method. The diaries depend on honest reporting, and some students stopped writing after the fifth week.",
  enterPrompt:
    "Paste or type at least two sentences to see how they open — and where the openings repeat.",
  results: "Results",
  sentences: "Sentences",
  distinct: "Different openers",
  topOpener: "Most common opener",
  topOpenerValue: "{word} · {share}",
  runs: "Runs of three or more",
  none: "None",
  headingsSkipped:
    "one:{count} heading was left out of the count.|other:{count} headings were left out of the count.",
  flagsHeading: "Patterns to look at",
  runText:
    "one:“{word}” opens {count} sentence in a row, starting at sentence {first}.|other:“{word}” opens {count} sentences in a row, starting at sentence {first}.",
  heavyText: "“{word}” opens {share} of your sentences.",
  expletiveText:
    "one:{count} sentence opens with “It is” or “There are” — try starting with the real subject.|other:{count} sentences open with “It is” or “There are” — try starting with the real subject.",
  bareThisText:
    "one:{count} sentence opens with “This” and a verb — name what “this” is (“This result shows…”).|other:{count} sentences open with “This” and a verb — name what “this” is (“This result shows…”).",
  noFlags:
    "No runs of the same opener and no heavy use of The, This, It, There or I.",
  typesHeading: "How your sentences open",
  typeSubject: "Subject first",
  typeTransition: "Transition",
  typeClause: "Introductory clause",
  typePhrase: "Introductory phrase",
  typeShare: "{count} · {share}",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with each sentence's opening word highlighted",
  hoverHint:
    "Opening words are coloured by type; repeated or overused openers are rose. Hover over one for details.",
  titleRun: "{type} — part of a run of the same opener",
  titleHeavy: "{type} — an opener you use heavily",
  openersHeading: "Opening words",
  twoWordHeading: "Opening two words",
  colOpener: "Opener",
  colSentences: "Sentences",
  colShare: "Share",
  tipsHeading: "Ways to vary an opening",
  tipClause:
    "Lead with a subordinate clause to put the condition first: “Although the sample was small, …”",
  tipTransition:
    "Lead with a transition when the sentence turns: “However, …”, “In contrast, …”",
  tipPhrase:
    "Lead with a prepositional phrase to set the scene: “In 2019, …”, “Across all groups, …”",
  tipSubject:
    "Swap “There are three factors that…” for the real subject: “Three factors…”",
} as const;

type Strings = typeof SENTENCE_OPENER_CHECKER_STRINGS;

const TYPE_TONES: Record<OpenerType, HighlightTone> = {
  subject: "amber",
  transition: "emerald",
  clause: "violet",
  phrase: "sky",
};

const TYPE_LABELS: Record<OpenerType, keyof Strings> = {
  subject: "typeSubject",
  transition: "typeTransition",
  clause: "typeClause",
  phrase: "typePhrase",
};

/** Solid fills for the share bars — the highlight classes carry padding a bar must not. */
const BAR_CLASSES: Record<OpenerType, string> = {
  subject: "bg-amber-300 dark:bg-amber-400/60",
  transition: "bg-emerald-300 dark:bg-emerald-400/60",
  clause: "bg-violet-300 dark:bg-violet-400/60",
  phrase: "bg-sky-300 dark:bg-sky-400/60",
};

const TYPE_ORDER: readonly OpenerType[] = [
  "subject",
  "transition",
  "clause",
  "phrase",
];

const MIN_SENTENCES = 2;
const TABLE_ROWS = 10;

function highlightFor(sentence: OpenerSentence, strings: Strings): TextMatch {
  const type = strings[TYPE_LABELS[sentence.type]];
  let title: string = type;
  if (sentence.inRun) {
    title = t(strings.titleRun, { type });
  } else if (sentence.heavy) {
    title = t(strings.titleHeavy, { type });
  }
  return {
    start: sentence.wordStart,
    end: sentence.wordEnd,
    category: sentence.inRun || sentence.heavy ? "flagged" : sentence.type,
    title,
  };
}

function Flags({
  report,
  strings,
  locale,
  fmt,
}: {
  report: OpenerReport;
  strings: Strings;
  locale: string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const percent = { style: "percent", maximumFractionDigits: 0 } as const;
  const items: { key: string; text: string }[] = [
    ...report.runs.map((run) => ({
      key: `run-${run.first}`,
      text: tPlural(strings.runText, locale, run.length, {
        word: run.label,
        count: fmt(run.length),
        first: fmt(run.first + 1),
      }),
    })),
    ...report.heavy.map((row) => ({
      key: `heavy-${row.key}`,
      text: t(strings.heavyText, {
        word: row.label,
        share: fmt(row.share, percent),
      }),
    })),
  ];
  if (report.expletives > 0) {
    items.push({
      key: "expletives",
      text: tPlural(strings.expletiveText, locale, report.expletives, {
        count: fmt(report.expletives),
      }),
    });
  }
  if (report.bareThis > 0) {
    items.push({
      key: "bare-this",
      text: tPlural(strings.bareThisText, locale, report.bareThis, {
        count: fmt(report.bareThis),
      }),
    });
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.flagsHeading}</h3>
      {items.length === 0 ? (
        <Notice>{strings.noFlags}</Notice>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li className={`${CARD} text-foreground text-sm`} key={item.key}>
              {item.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TypeBars({
  report,
  strings,
  fmt,
}: {
  report: OpenerReport;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const total = report.sentences.length;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.typesHeading}</h3>
      <ul className="space-y-3">
        {TYPE_ORDER.map((type) => {
          const count = report.types[type];
          const share = total > 0 ? count / total : 0;
          return (
            <li className="space-y-1" key={type}>
              <div className="flex justify-between gap-3 text-sm">
                <span className={LABEL}>{strings[TYPE_LABELS[type]]}</span>
                <span className="text-muted-foreground">
                  {t(strings.typeShare, {
                    count: fmt(count),
                    share: fmt(share, {
                      style: "percent",
                      maximumFractionDigits: 0,
                    }),
                  })}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full ${BAR_CLASSES[type]}`}
                  style={{ width: `${share * 100}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function SentenceOpenerCheckerWidget() {
  const strings = useToolUi(SENTENCE_OPENER_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => analyzeSentenceOpeners(deferred), [deferred]);

  const percent = { style: "percent", maximumFractionDigits: 0 } as const;
  const top = report.openers[0];
  const highlights = report.sentences.map((sentence) =>
    highlightFor(sentence, strings)
  );
  const tones: Record<string, HighlightTone> = {
    ...TYPE_TONES,
    flagged: "rose",
  };
  const shareRows = (rows: OpenerReport["openers"]) =>
    rows
      .slice(0, TABLE_ROWS)
      .map((row) => [row.label, fmt(row.count), fmt(row.share, percent)]);
  const ready = report.sentences.length >= MIN_SENTENCES;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="so-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {ready && top ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-2">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.topOpener}
                value={t(strings.topOpenerValue, {
                  word: top.label,
                  share: fmt(top.share, percent),
                })}
              />
              <StatCard
                label={strings.sentences}
                value={fmt(report.sentences.length)}
              />
              <StatCard
                label={strings.distinct}
                value={fmt(report.openers.length)}
              />
              <StatCard
                label={strings.runs}
                value={
                  report.runs.length > 0
                    ? fmt(report.runs.length)
                    : strings.none
                }
              />
            </StatGrid>
            {report.skipped > 0 ? (
              <p className={HINT}>
                {tPlural(strings.headingsSkipped, locale, report.skipped, {
                  count: fmt(report.skipped),
                })}
              </p>
            ) : null}
          </div>

          <Flags fmt={fmt} locale={locale} report={report} strings={strings} />

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              {TYPE_ORDER.map((type) => (
                <span className={HIGHLIGHT_TONES[TYPE_TONES[type]]} key={type}>
                  {strings[TYPE_LABELS[type]]}
                </span>
              ))}
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={tones}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          <TypeBars fmt={fmt} report={report} strings={strings} />

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.openersHeading}</h3>
              <DataTable
                head={[
                  strings.colOpener,
                  strings.colSentences,
                  strings.colShare,
                ]}
                rows={shareRows(report.openers)}
              />
            </div>
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.twoWordHeading}</h3>
              <DataTable
                head={[
                  strings.colOpener,
                  strings.colSentences,
                  strings.colShare,
                ]}
                rows={shareRows(report.twoWordOpeners)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.tipsHeading}</h3>
            <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
              <li>{strings.tipClause}</li>
              <li>{strings.tipTransition}</li>
              <li>{strings.tipPhrase}</li>
              <li>{strings.tipSubject}</li>
            </ul>
          </div>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
